<template>
  <div>
    <Head title="Crear Aviso de Cobro" />

    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button class="mr-2 p-3 text-white bg-orange-800 hover:bg-orange-900 rounded-lg transition-colors" title="Volver atrás" @click="back()">
        <i class="fa fa-arrow-left" />
      </button>
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold">Crear Aviso de Cobro</h1>
        <p class="text-sm text-gray-500 mt-1">Las órdenes se crearán en estado <span class="font-semibold text-orange-600">Pendiente por Pago</span></p>
      </div>
    </div>

    <!-- Wizard Steps -->
    <div class="flex flex-col sm:flex-row items-center justify-center mb-8 gap-4 sm:gap-0">
      <div v-for="step in steps" :key="step.id" class="flex items-center">
        <div :class="['w-10 h-10 flex items-center justify-center rounded-full text-lg font-bold', currentStep === step.id ? 'bg-orange-700 text-white' : completedSteps.includes(step.id) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600']">
          <i v-if="completedSteps.includes(step.id)" class="fa fa-check text-sm" />
          <span v-else>{{ step.id }}</span>
        </div>
        <span class="ml-2 mr-4 font-semibold" :class="currentStep === step.id ? 'text-orange-700' : 'text-gray-500'">{{ step.label }}</span>
        <span v-if="step.id < steps.length" class="hidden sm:block w-8 h-1 bg-gray-300 mx-1 rounded" />
      </div>
    </div>

    <!-- ======================== PASO 1: Clientes ======================== -->
    <div v-if="currentStep === 1">
      <!-- Lista de clientes ya seleccionados -->
      <div class="mb-4">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-orange-800 flex items-center gap-2">
            <i class="fa fa-users text-orange-500" />
            Clientes seleccionados
            <span class="ml-1 inline-flex items-center justify-center w-6 h-6 bg-orange-600 text-white text-xs font-bold rounded-full">
              {{ selectedClients.length }}
            </span>
          </h2>
        </div>

        <!-- Clientes vacíos -->
        <div
          v-if="selectedClients.length === 0"
          class="border-2 border-dashed border-orange-200 rounded-2xl py-10 text-center text-gray-400 bg-orange-50/30"
        >
          <i class="fa fa-user-plus text-3xl text-orange-200 block mb-2" />
          <p class="text-sm">Aún no has agregado clientes.</p>
          <p class="text-xs mt-1">Usa el botón de abajo para agregar.</p>
        </div>

        <!-- Lista de clientes -->
        <div v-else class="space-y-2">
          <div
            v-for="(client, idx) in selectedClients"
            :key="idx"
            class="flex items-center gap-3 bg-white border border-orange-100 rounded-xl px-4 py-3 shadow-sm"
          >
            <div class="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <i class="fa fa-user text-orange-500 text-sm" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-gray-800 truncate">{{ client.name }}</div>
              <div class="text-xs text-gray-400 truncate">
                <span v-if="client.identification">{{ client.identification }}</span>
                <span v-if="client.phone"> · {{ client.phone }}</span>
                <span v-if="client.email"> · {{ client.email }}</span>
              </div>
            </div>
            <button
              class="flex-shrink-0 w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Quitar cliente"
              @click="removeClient(idx)"
            >
              <i class="fa fa-times text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Panel buscador de cliente -->
      <div v-if="showClientSearch" class="bg-white border-2 border-orange-300 rounded-2xl shadow-xl mb-4 overflow-hidden">
        <div class="flex items-center justify-between px-5 py-3 bg-orange-50 border-b border-orange-100">
          <span class="font-semibold text-orange-700 flex items-center gap-2">
            <i class="fa fa-search text-orange-400" />
            Buscar cliente
          </span>
          <button class="text-gray-400 hover:text-gray-600 transition" @click="closeClientSearch">
            <i class="fa fa-times" />
          </button>
        </div>
        <div class="p-4">
          <!-- Input búsqueda -->
          <div class="relative mb-3">
            <input
              ref="clientSearchInput"
              v-model="clientSearch"
              type="text"
              placeholder="Escribe nombre, cédula o alias..."
              class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition"
              @input="searchClients"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <span v-if="clientSearchLoading" class="absolute right-3 top-1/2 -translate-y-1/2">
              <i class="fa fa-spinner fa-spin text-orange-400 text-sm" />
            </span>
          </div>

          <!-- Resultados -->
          <div class="max-h-56 overflow-y-auto space-y-1">
            <button
              v-for="client in clientResults"
              :key="client.id"
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-orange-50 transition text-left group"
              @click="selectClientFromSearch(client)"
            >
              <div class="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <i class="fa fa-user text-orange-400 text-xs" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-gray-800 text-sm group-hover:text-orange-700 truncate">
                  {{ client.name }}
                </div>
                <div class="text-xs text-gray-400 truncate">
                  {{ client.identification }}
                  <span v-if="client.phone"> · {{ client.phone }}</span>
                </div>
              </div>
              <span v-if="isClientSelected(client)" class="text-xs text-green-600 font-semibold flex-shrink-0">
                <i class="fa fa-check-circle" /> Agregado
              </span>
            </button>

            <div
              v-if="clientResults.length === 0 && clientSearch.length > 0 && !clientSearchLoading"
              class="py-6 text-center text-gray-400 text-sm"
            >
              <i class="fa fa-user-times text-2xl text-gray-200 block mb-1" />
              No se encontraron clientes.<br />
              <button class="mt-2 text-orange-600 font-semibold hover:underline text-xs" @click="showCreateClientForm = !showCreateClientForm">
                <i class="fa fa-plus mr-1" /> Crear nuevo cliente
              </button>
            </div>

            <div
              v-if="clientResults.length === 0 && clientSearch.length === 0 && clientSearchLoading"
              class="py-6 text-center text-gray-400 text-sm"
            >
              <i class="fa fa-spinner fa-spin text-orange-300 text-2xl block mb-1" />
              Cargando clientes...
            </div>
          </div>

          <!-- Formulario rápido de nuevo cliente -->
          <div v-if="showCreateClientForm" class="mt-4 border-t border-gray-100 pt-4">
            <p class="text-xs font-semibold text-orange-600 mb-3 uppercase tracking-wider">Crear nuevo cliente</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
                <input
                  v-model="newClient.name" type="text" placeholder="Nombre completo"
                  class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Cédula / RIF *</label>
                <input
                  v-model="newClient.identification" type="text" placeholder="Ej: V-12345678"
                  class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                <input
                  v-model="newClient.phone" type="text" placeholder="04XX-XXXXXXX"
                  class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  v-model="newClient.email" type="email" placeholder="correo@ejemplo.com"
                  class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>
            </div>
            <div class="flex gap-2 mt-3">
              <button
                :disabled="savingClient" class="flex-1 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-60"
                @click="saveNewClient"
              >
                <i class="fa fa-save mr-1" />
                {{ savingClient ? 'Guardando...' : 'Guardar y Agregar' }}
              </button>
              <button
                class="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
                @click="showCreateClientForm = false"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Botón principal "Agregar nuevo cliente" -->
      <button
        class="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 text-orange-600 hover:text-orange-700 font-semibold rounded-2xl transition-all text-base"
        @click="openClientSearch"
      >
        <i class="fa fa-user-plus text-lg" />
        Agregar nuevo cliente
      </button>

      <!-- Botón Siguiente -->
      <div class="flex justify-end mt-6">
        <button
          class="btn-blue w-full sm:w-auto px-8 py-4 text-lg shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="selectedClients.length === 0"
          @click="tryNextStep(2)"
        >
          Siguiente <i class="fa fa-arrow-right ml-2" />
        </button>
      </div>
      <div v-if="stepWarning && currentStep === 1" class="text-red-600 text-sm mt-2 text-center">{{ stepWarning }}</div>
    </div>



    <!-- ======================== PASO 2: Productos ======================== -->
    <div v-if="currentStep === 2">
      <div class="mb-6 text-center">
        <h2 class="text-xl sm:text-2xl font-bold text-orange-800 mb-1 flex items-center justify-center gap-2">
          <i class="fa fa-shopping-cart text-orange-500" /> Productos y Descuento
        </h2>
        <p class="text-gray-500">Estos productos/servicios se aplicarán a todos los clientes seleccionados.</p>
      </div>

      <!-- Acciones -->
      <div class="flex flex-col sm:flex-row gap-4 mb-4">
        <button class="flex-1 btn-blue flex items-center justify-center gap-2 py-4 text-lg shadow-md" @click="listProducts = !listProducts">
          <i class="fa fa-list" /> Lista de productos
        </button>
        <button class="flex-1 btn-blue flex items-center justify-center gap-2 py-4 text-lg shadow-md" @click="addQuickProduct()">
          <i class="fa fa-plus" /> Servicio o producto rápido
        </button>
      </div>

      <!-- Buscador -->
      <div class="mb-6">
        <text-input v-model="searchTerms" placeholder="Buscar productos..." class="w-full text-lg border-2 border-orange-200 rounded-lg shadow-sm" input-class="focus:ring-orange-500 focus:border-orange-500">
          <template #append><i class="fa fa-search text-orange-400" /></template>
        </text-input>
      </div>

      <!-- Lista de productos -->
      <transition name="fade">
        <div v-if="listProducts" class="mb-8">
          <div class="bg-white border border-orange-100 rounded-2xl shadow-xl max-h-80 overflow-y-auto p-4">
            <table class="w-full text-base">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th class="p-3 text-left">Código</th>
                  <th class="p-3 text-left">Producto</th>
                  <th class="p-3 text-center">Disponibles</th>
                  <th class="p-3 text-right">Precio</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in products.data" :key="product.id" class="hover:bg-orange-50 cursor-pointer border-t transition" @click="selectProduct(product)">
                  <td class="p-3 font-mono">{{ product.code }}</td>
                  <td class="p-3">{{ product.name }}</td>
                  <td class="p-3 text-center">{{ product.type_product == 'product' ? (product?.inventory?.quantity || 0) : 'N/A' }}</td>
                  <td class="p-3 text-right text-orange-700 font-semibold">${{ Number(product?.prices?.[0]?.price || 0).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </transition>

      <!-- Carrito -->
      <div class="bg-white rounded-xl shadow-md border border-orange-100 mb-8 overflow-x-auto">
        <table class="w-full text-base">
          <thead class="bg-orange-50">
            <tr>
              <th class="p-3 text-left">Código</th>
              <th class="p-3 text-left">Producto</th>
              <th class="p-3 text-center">Cant.</th>
              <th class="p-3 text-right">Precio</th>
              <th class="p-3 text-right">Total</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in cart" :key="index" class="border-t hover:bg-gray-50 transition">
              <td class="p-3">
                <text-input v-if="product.quick" v-model="cart[index].code" placeholder="Código" class="w-full" />
                <span v-else class="font-mono">{{ product.code }}</span>
              </td>
              <td class="p-3">
                <text-input v-if="product.quick" v-model="cart[index].name" placeholder="Nombre" class="w-full mb-1" />
                <span v-else>{{ product.name }}</span>
                <div v-if="product.type_product === 'service' || product.quick">
                  <textarea
                    v-model="cart[index].description" rows="2"
                    class="form-textarea w-full rounded-lg border-gray-300 mt-1 text-sm focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Descripción del servicio..."
                  />
                </div>
              </td>
              <td class="p-3">
                <text-input v-model="cart[index].quantity" type="number" class="w-20 text-center" />
              </td>
              <td class="p-3">
                <text-input v-model="cart[index].price" type="number" class="w-24 text-right" />
              </td>
              <td class="p-3 text-right font-semibold text-orange-700">
                ${{ (parseFloat(cart[index].price || 0) * parseFloat(cart[index].quantity || 1)).toFixed(2) }}
              </td>
              <td class="p-3 text-center">
                <button class="p-2 text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors shadow" @click="removeProduct(index)">
                  <i class="fa fa-trash" />
                </button>
              </td>
            </tr>
            <tr v-if="cart.length === 0">
              <td colspan="6" class="py-10 text-center text-gray-400 italic">No has agregado productos aún.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totales y descuento -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div class="bg-orange-50 border border-orange-100 rounded-xl p-6 shadow flex flex-col gap-2">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-orange-700">Subtotal:</span>
            <span class="font-medium">${{ getSubtotal() }}</span>
          </div>
          <div v-if="getDiscountAmount() > 0" class="flex justify-between items-center text-red-600">
            <span>Descuento:</span>
            <span>- ${{ getDiscountAmount() }}</span>
          </div>
          <div class="flex justify-between items-center font-bold text-xl mt-2">
            <span>Total por cliente:</span>
            <span class="text-orange-800">${{ getTotal() }}</span>
          </div>
          <div class="text-xs text-gray-400 mt-1">
            Total {{ selectedClients.length }} cliente(s):
            <strong class="text-orange-700">${{ (parseFloat(getTotal()) * selectedClients.length).toFixed(2) }}</strong>
          </div>
        </div>
        <div class="bg-white border border-orange-100 rounded-xl p-6 shadow flex flex-col gap-4">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-percent text-orange-500" />
            <span class="font-semibold text-orange-700">Aplicar descuento</span>
          </div>
          <select v-model="discount.type" class="form-select rounded-lg border-gray-300 focus:ring-orange-500 focus:border-orange-500">
            <option value="percentage">Porcentaje (%)</option>
            <option value="amount">Monto fijo ($)</option>
          </select>
          <text-input
            v-model="discount.value"
            :label="discount.type === 'percentage' ? 'Porcentaje (%)' : 'Monto ($)'"
            type="number" :step="discount.type === 'percentage' ? '1' : '0.01'" :min="0"
            :max="discount.type === 'percentage' ? 100 : null" class="w-full"
          />
        </div>
      </div>

      <div class="flex flex-col sm:flex-row justify-between gap-4 mt-4">
        <button class="btn-blue px-8 py-4 text-lg w-full sm:w-auto" @click="prevStep">
          <i class="fa fa-arrow-left mr-2" /> Anterior
        </button>
        <button
          class="btn-blue px-8 py-4 text-lg w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="cart.length === 0" @click="tryNextStep(3)"
        >
          Siguiente <i class="fa fa-arrow-right ml-2" />
        </button>
      </div>
      <div v-if="stepWarning && currentStep === 2" class="text-red-600 text-sm mt-2 text-center">{{ stepWarning }}</div>
    </div>

    <!-- ======================== PASO 3: Confirmación ======================== -->
    <div v-if="currentStep === 3">
      <div class="mb-6 text-center">
        <h2 class="text-xl sm:text-2xl font-bold text-orange-800 mb-1">Resumen y Confirmación</h2>
        <p class="text-gray-500">Revisa el aviso antes de generarlo. Se creará una orden por cada cliente.</p>
      </div>

      <!-- Resumen de clientes -->
      <div class="bg-white rounded-xl shadow p-4 border border-orange-100 mb-6">
        <div class="flex items-center gap-2 mb-3">
          <i class="fa fa-users text-xl text-orange-500" />
          <span class="font-semibold text-orange-800">Clientes ({{ selectedClients.length }})</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <span v-for="(c, i) in selectedClients" :key="i" class="px-3 py-1 bg-orange-50 border border-orange-200 rounded-full text-sm font-medium text-orange-800">
            {{ c.name }}
          </span>
        </div>
      </div>

      <!-- Resumen de productos -->
      <div class="bg-white rounded-xl shadow p-4 border border-orange-100 mb-6 overflow-x-auto">
        <div class="flex items-center gap-2 mb-3">
          <i class="fa fa-boxes text-xl text-orange-500" />
          <span class="font-semibold text-orange-800">Productos / Servicios</span>
        </div>
        <table class="w-full text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="p-2 text-left">Producto</th>
              <th class="p-2 text-center">Cantidad</th>
              <th class="p-2 text-right">Precio</th>
              <th class="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, idx) in cart" :key="idx" class="border-b">
              <td class="p-2">{{ product.name }}</td>
              <td class="p-2 text-center">{{ product.quantity }}</td>
              <td class="p-2 text-right">${{ Number(product.price).toFixed(2) }}</td>
              <td class="p-2 text-right">${{ (product.price * product.quantity).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-between font-bold text-lg mt-4 pt-3 border-t border-orange-100">
          <span>Total por cliente:</span>
          <span class="text-orange-700">${{ getTotal() }}</span>
        </div>
      </div>

      <!-- Observaciones y fecha -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6 space-y-4">
        <text-input v-model="form.date" label="Fecha" type="date" required />
        <text-area-input v-model="form.comentary" label="Observaciones" placeholder="Indicaciones del aviso de cobro..." />
      </div>

      <!-- Advertencia estado -->
      <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <i class="fa fa-exclamation-triangle text-yellow-500 text-xl mt-0.5" />
        <div>
          <p class="font-semibold text-yellow-800">Las órdenes quedarán en estado "Estimado / Pendiente por Pago"</p>
          <p class="text-sm text-yellow-700 mt-1">Se generará <strong>{{ selectedClients.length }} orden(es)</strong> con el mismo detalle de productos, cada una asociada a su respectivo cliente.</p>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row justify-between gap-4">
        <button class="btn-blue px-8 py-4 text-lg w-full sm:w-auto" @click="prevStep">
          <i class="fa fa-arrow-left mr-2" /> Anterior
        </button>
        <button :disabled="loading" class="btn-blue text-lg px-8 py-4 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed" @click="generateAvisos">
          <i class="fa fa-file-invoice mr-2" />
          <span v-if="loading">Generando...</span>
          <span v-else>Generar Aviso de Cobro</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../../Shared/Layout.vue'
import TextInput from '@/Shared/TextInput'
import TextAreaInput from '@/Shared/TextAreaInput'
import { Head } from '@inertiajs/inertia-vue3'
import axios from 'axios'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  components: { TextInput, TextAreaInput, Head },
  layout: Layout,

  props: {
    products: Object,
    change: Number,
  },

  data() {
    return {
      currentStep: 1,
      completedSteps: [],
      stepWarning: '',
      loading: false,

      steps: [
        { id: 1, label: 'Clientes' },
        { id: 2, label: 'Productos' },
        { id: 3, label: 'Confirmación' },
      ],

      // ── Clientes ──────────────────────────────────────
      selectedClients: [],
      showClientSearch: false,
      clientSearch: '',
      clientResults: [],
      clientSearchLoading: false,
      showCreateClientForm: false,
      savingClient: false,
      newClient: { name: '', identification: '', phone: '', email: '' },

      // ── Productos ─────────────────────────────────────
      cart: [],
      listProducts: false,
      searchTerms: '',

      discount: {
        type: 'percentage',
        value: 0,
      },

      form: {
        date: new Date().toISOString().substr(0, 10),
        comentary: '',
      },
    }
  },

  watch: {
    searchTerms: throttle(function (val) {
      this.$inertia.get(
        '/cobranzas/aviso/create',
        pickBy({ search: { terms: val } }),
        { preserveState: true, preserveScroll: true, replace: true },
      )
    }, 300),
  },

  methods: {
    back() { window.history.back() },

    // ── Wizard ────────────────────────────────────────
    tryNextStep(step) {
      this.stepWarning = ''
      if (step === 2 && this.selectedClients.length === 0) {
        this.stepWarning = 'Debes seleccionar al menos un cliente.'
        return
      }
      if (step === 3 && this.cart.length === 0) {
        this.stepWarning = 'Debes agregar al menos un producto o servicio.'
        return
      }
      if (!this.completedSteps.includes(this.currentStep)) {
        this.completedSteps.push(this.currentStep)
      }
      this.currentStep++
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
        this.stepWarning = ''
      }
    },

    // ── Búsqueda de clientes ──────────────────────────
    openClientSearch() {
      this.showClientSearch = true
      this.clientSearch = ''
      this.clientResults = []
      this.showCreateClientForm = false
      this.$nextTick(() => {
        if (this.$refs.clientSearchInput) {
          this.$refs.clientSearchInput.focus()
        }
      })
      // Cargar todos los clientes al abrir
      this.fetchClients()
    },

    closeClientSearch() {
      this.showClientSearch = false
      this.clientSearch = ''
      this.clientResults = []
      this.showCreateClientForm = false
    },

    async fetchClients(search = '') {
      this.clientSearchLoading = true
      try {
        const url = search
          ? `/getClients?search=${encodeURIComponent(search)}`
          : '/getClients'
        const res = await axios.get(url)
        this.clientResults = res.data.data || []
      } catch (e) {
        this.clientResults = []
      } finally {
        this.clientSearchLoading = false
      }
    },

    searchClients: throttle(function () {
      this.fetchClients(this.clientSearch)
    }, 300),

    selectClientFromSearch(client) {
      const exists = this.selectedClients.some(c => c.identification === client.identification)
      if (!exists) {
        this.selectedClients.push(client)
      }
      // Cerrar panel y limpiar para poder agregar el siguiente
      this.closeClientSearch()
    },

    isClientSelected(client) {
      return this.selectedClients.some(c => c.identification === client.identification)
    },

    removeClient(idx) {
      this.selectedClients.splice(idx, 1)
    },

    // ── Crear cliente nuevo inline ─────────────────────
    async saveNewClient() {
      if (!this.newClient.name || !this.newClient.identification) return
      this.savingClient = true
      try {
        const res = await axios.post('/saveClient', this.newClient)
        const client = res.data.data
        const exists = this.selectedClients.some(c => c.identification === client.identification)
        if (!exists) {
          this.selectedClients.push(client)
        }
        this.newClient = { name: '', identification: '', phone: '', email: '' }
        this.closeClientSearch()
      } catch (e) {
        console.error(e)
      } finally {
        this.savingClient = false
      }
    },

    // ── Productos ──────────────────────────────────────
    selectProduct(product) {
      if (product.type_product === 'product' && product?.inventory?.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }
      const exists = this.cart.some(p => p.id === product.id)
      if (!exists) {
        this.cart.push({
          ...product,
          quantity: 1,
          price: product?.prices?.[0]?.price || 0,
          fee: 0,
          quick: false,
          stock: product.type_product === 'product' ? (product?.inventory?.quantity || 0) : 'N/A',
        })
      }
      this.listProducts = false
    },

    addQuickProduct() {
      this.cart.push({
        id: 'quick_' + Date.now(),
        code: '',
        name: '',
        description: '',
        quantity: 1,
        price: 0,
        fee: 0,
        type_product: 'service',
        quick: true,
      })
    },

    removeProduct(index) {
      this.cart.splice(index, 1)
    },

    getSubtotal() {
      return this.cart.reduce((t, p) => t + parseFloat(p.price || 0) * parseFloat(p.quantity || 1), 0).toFixed(2)
    },

    getDiscountAmount() {
      const subtotal = parseFloat(this.getSubtotal())
      if (this.discount.type === 'percentage') {
        return ((subtotal * parseFloat(this.discount.value || 0)) / 100).toFixed(2)
      }
      return Math.min(parseFloat(this.discount.value || 0), subtotal).toFixed(2)
    },

    getTotal() {
      return (parseFloat(this.getSubtotal()) - parseFloat(this.getDiscountAmount())).toFixed(2)
    },

    generateAvisos() {
      if (this.loading) return
      this.loading = true

      this.$inertia.post('/cobranzas/aviso', {
        clients: this.selectedClients,
        products: this.cart,
        discount: this.discount,
        date: this.form.date,
        comentary: this.form.comentary,
        change: this.change,
      }, {
        onSuccess: () => { this.loading = false },
        onError: () => { this.loading = false },
      })
    },
  },
}
</script>

